import { memo, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { sweepDaaluuTableTiles, type DaaluuPoint, type DaaluuShuffleCommand, type DaaluuShuffleView } from '@big2/shared';
import './daaluu.css';

interface Props {
  view: DaaluuShuffleView;
  mySeat: number;
  players: { name: string; seat: number; isBot: boolean }[];
  deadline: number | null;
  connected: boolean;
  lang: 'mn' | 'en';
  /** Supplied by the application's socket context; no component transport. */
  onSweep: (command: DaaluuShuffleCommand) => void;
  onRetry: () => void;
  canRetry: boolean;
  embedded?: boolean;
}

/** Original local SVG artwork: anatomical palm, five fingers, wrist and shading. */
export const MixingHand = memo(function MixingHand() {
  const id = useId().replace(/:/g, '');
  return <svg viewBox="0 0 120 190" aria-hidden="true" focusable="false">
    <defs><linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
      <stop offset="0" stopColor="#a86443" /><stop offset=".45" stopColor="#e8b18a" /><stop offset="1" stopColor="#c3835d" />
    </linearGradient></defs>
    <path fill={`url(#${id})`} stroke="#9e6348" strokeWidth="1.5" d="M37 187 L34 126 C27 115 22 110 16 95 L5 69 C2 61 8 55 14 61 L29 80 L27 37 C26 27 37 24 40 35 L45 69 L44 18 C44 7 56 6 58 17 L62 65 L67 13 C69 3 81 7 80 19 L77 69 L85 31 C88 21 99 25 97 37 L89 89 C96 102 89 120 82 130 L83 187 Z" />
    <g fill="none" stroke="#a96f52" strokeWidth="1.3" opacity=".5">
      <path d="M37 83 Q54 74 73 83 M37 95 Q48 114 70 107 M44 126 Q64 133 78 125 M45 143 L73 145" />
      <path d="M29 48 L38 47 M45 37 L56 36 M67 33 L78 34 M83 52 L93 54" />
    </g>
    <g fill="#f1c7a6" opacity=".8"><ellipse cx="33" cy="35" rx="4" ry="5" /><ellipse cx="51" cy="18" rx="4" ry="6" /><ellipse cx="73" cy="15" rx="4" ry="6" /><ellipse cx="90" cy="33" rx="4" ry="5" /></g>
  </svg>;
});

const ShuffleTileBack = memo(function ShuffleTileBack({ x, y, angle }: {
  x: number; y: number; angle: number;
}) {
  return <div className="daaluu-tile-back" aria-hidden="true"
    style={{ '--tile-x': x * 100, '--tile-y': y * 100, '--tile-angle': `${angle}deg` } as CSSProperties}>
  </div>;
});

export function DaaluuShuffleTable({ view, mySeat, players, deadline, connected, lang, onSweep, onRetry, canRetry, embedded = false }: Props) {
  const mn = lang === 'mn';
  const instructionsId = useId();
  const surface = useRef<HTMLDivElement>(null);
  const contact = useRef<DaaluuPoint | null>(null);
  const pointerId = useRef<number | null>(null);
  const sequence = useRef(-1);
  const lastSentAt = useRef(0);
  const [localHand, setLocalHand] = useState<(DaaluuPoint & { angle?: number }) | null>(null);
  const [tiles, setTiles] = useState(view.tiles);
  const canMix = connected && view.stage === 'mixing' && mySeat !== view.callerSeat && deadline !== null && deadline > Date.now();

  useEffect(() => {
    setTiles(view.tiles);
    sequence.current = Math.max(sequence.current, view.acceptedSequences[mySeat] ?? -1);
  }, [view.tiles, view.acceptedSequences, mySeat]);

  useEffect(() => {
    const element = surface.current;
    const captured = pointerId.current;
    contact.current = null;
    pointerId.current = null;
    sequence.current = view.acceptedSequences[mySeat] ?? -1;
    setLocalHand(null);
    return () => {
      if (captured !== null && element?.hasPointerCapture(captured)) element.releasePointerCapture(captured);
    };
    // A retry is a new command epoch. Do not restart this effect per movement.
  }, [view.roundId, view.attempt, mySeat]);

  useEffect(() => {
    if (!canMix) {
      const captured = pointerId.current;
      if (captured !== null && surface.current?.hasPointerCapture(captured)) surface.current.releasePointerCapture(captured);
      pointerId.current = null;
      contact.current = null;
      setLocalHand(null);
    }
  }, [canMix]);

  function send(point: DaaluuPoint, touching: boolean) {
    onSweep({ ...point, roundId: view.roundId, attempt: view.attempt, sequence: ++sequence.current, contact: touching });
  }

  function pointFor(event: PointerEvent<HTMLDivElement>): DaaluuPoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width))),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / Math.max(1, rect.height))) };
  }

  function start(event: PointerEvent<HTMLDivElement>) {
    if (!canMix || pointerId.current !== null || event.button !== 0) return;
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerId.current = event.pointerId;
    const point = pointFor(event);
    contact.current = point;
    setLocalHand(point);
    send(point, true);
    lastSentAt.current = performance.now();
  }

  function move(event: PointerEvent<HTMLDivElement>) {
    if (!canMix || pointerId.current !== event.pointerId || !contact.current) return;
    const point = pointFor(event);
    setLocalHand({ ...point, angle: Math.atan2(point.y - contact.current.y, point.x - contact.current.x) * 180 / Math.PI + 90 });
    if (performance.now() - lastSentAt.current < 50) return;
    sweep(point);
  }

  function sweep(target: DaaluuPoint) {
    if (!contact.current) return;
    const dx = target.x - contact.current.x, dy = target.y - contact.current.y;
    const scale = Math.min(1, 0.35 / Math.max(0.35, Math.hypot(dx, dy)));
    const point = { x: contact.current.x + dx * scale, y: contact.current.y + dy * scale };
    const from = contact.current;
    setTiles(current => sweepDaaluuTableTiles(current, from, point).tiles);
    contact.current = point;
    send(point, true);
    lastSentAt.current = performance.now();
  }

  function stop(event?: PointerEvent<HTMLDivElement>) {
    if (event && pointerId.current !== event.pointerId) return;
    if (contact.current && canMix) send(contact.current, false);
    const captured = pointerId.current;
    pointerId.current = null;
    contact.current = null;
    setLocalHand(null);
    if (captured !== null && surface.current?.hasPointerCapture(captured)) surface.current.releasePointerCapture(captured);
  }

  function keyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (!canMix) return;
    if (event.key === 'Escape') { stop(); return; }
    const moves: Record<string, DaaluuPoint> = { ArrowLeft: { x: -.055, y: 0 }, ArrowRight: { x: .055, y: 0 }, ArrowUp: { x: 0, y: -.055 }, ArrowDown: { x: 0, y: .055 } };
    const delta = moves[event.key];
    if (!delta) return;
    event.preventDefault();
    if (event.repeat && performance.now() - lastSentAt.current < 50) return;
    if (!contact.current) {
      const initial = { x: .45, y: .44 };
      contact.current = initial;
      send(initial, true);
    }
    const point = { x: Math.max(.04, Math.min(.96, contact.current.x + delta.x)), y: Math.max(.04, Math.min(.96, contact.current.y + delta.y)) };
    setLocalHand(point);
    sweep(point);
  }

  const caller = players.find(player => player.seat === view.callerSeat)?.name ?? '';
  return <section className="daaluu-preparation" aria-label={mn ? 'Даалуу холих ширээ' : 'Daaluu shuffle table'}>
    {!embedded && <header className="daaluu-table-heading">
      <div><span className="daaluu-eyebrow">{mn ? 'МОД ХОЛИХ' : 'MIX THE TILES'}</span><h2>{mn ? 'Даалуу' : 'Daaluu'}</h2></div>
    </header>}
    <p id={instructionsId} className="daaluu-instructions">
      {mySeat === view.callerSeat
        ? mn ? `${caller} — жанлий дуудах тул мод холихгүй.` : `${caller} is the caller and sits out mixing.`
        : mn ? <>Чирч эсвэл шударч модоо холино.<span className={embedded ? 'daaluu-sr-only' : undefined}> Гарын сумтай товч ашиглаж болно; Esc дарж гараа авна.</span></>
          : <>Drag or swipe to mix tiles.<span className={embedded ? 'daaluu-sr-only' : undefined}> Keyboard: arrow keys to sweep; Escape to lift your hand.</span></>}
    </p>
    <div ref={surface} className={`daaluu-mixing-surface${canMix ? ' can-mix' : ''}`} tabIndex={canMix ? 0 : -1}
      role="group" aria-label={mn ? 'Мод холих талбай' : 'Interactive tile mixing surface'} aria-describedby={instructionsId}
      onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop}
      onLostPointerCapture={() => stop()} onKeyDown={keyboard} onBlur={() => stop()}>
      {tiles.map(tile => <ShuffleTileBack key={tile.handle} x={tile.x} y={tile.y} angle={tile.angle} />)}
      {view.gestures.filter(hand => hand.seat !== mySeat).map(hand => <div key={hand.seat} className="daaluu-mixing-hand"
        style={{ '--hand-x': hand.x * 100, '--hand-y': hand.y * 100, '--hand-angle': `${hand.angle}deg` } as CSSProperties}>
        <MixingHand /><span>{players.find(player => player.seat === hand.seat)?.name}</span>
      </div>)}
      {localHand && <div className="daaluu-mixing-hand is-local" style={{ '--hand-x': localHand.x * 100, '--hand-y': localHand.y * 100, '--hand-angle': `${localHand.angle ?? 0}deg` } as CSSProperties}><MixingHand /></div>}
      {view.stage === 'ready' && <div className="daaluu-stacks" aria-label={mn ? 'Өрсөн мод' : 'Distribution stacks'}>
        {view.stacks.map(stack => <div key={stack.handle} className="daaluu-stack"><span className="daaluu-tile-back"></span><span>{stack.count}</span></div>)}
      </div>}
    </div>
    <ul className={`daaluu-participants${embedded ? ' daaluu-sr-only' : ''}`} aria-label={mn ? 'Мод холисон тоглогчид' : 'Shuffle participation'}>
      {players.map(player => <li key={player.seat} className={view.participatedSeats.includes(player.seat) ? 'has-mixed' : ''}>
        {player.name}{player.isBot ? mn ? ' (Бот)' : ' (Bot)' : ''} · {player.seat === view.callerSeat ? mn ? 'Дуудагч' : 'Caller' : view.participatedSeats.includes(player.seat) ? mn ? 'Холисон' : 'Mixed' : mn ? 'Хүлээж байна' : 'Waiting'}
      </li>)}
    </ul>
    {view.stage === 'retry' && <div className="daaluu-retry" role="status">
      <p>{mn ? 'Холих оролцоо дутуу. Дахин холино уу.' : 'Shuffle participation is incomplete. Retry mixing.'}</p>
      {canRetry && <button className="btn btn-primary" onClick={onRetry}>{view.attempt >= 3 ? mn ? 'Өрөө рүү буцах' : 'Return to lobby' : mn ? 'Дахин холих' : 'Retry shuffle'}</button>}
    </div>}
  </section>;
}
